// src/components/ProductionDashboard/WorkerPerformance.js
// Task 12 â€” Worker Performance Stars, KPIs & Daily Activity Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import {
    Star, Trophy, TrendingUp, BarChart3, Users, ArrowLeft,
    Clock, CheckCircle2, Award, Calendar,
    ChevronRight, Gem, Package, Layers, User, Activity,
    Target, Medal, Crown, RefreshCcw, Search,
    ArrowUpRight, ArrowDownRight, Minus, Menu
} from 'lucide-react';

import { productionApi } from '../api';
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';
import { toRatingHistorySeries } from '../../utils/workerRatings';

const WorkerService = {
    getLeaderboard: async (period = 'month') => {
        try {
            const res = await productionApi.get(`/workers/leaderboard?period=${period}`);
            return res.data;
        } catch (e) {
            console.error('Leaderboard error:', e);
            return { success: false, data: [] };
        }
    },
    getPerformance: async (workerId, period = 'month') => {
        try {
            const res = await productionApi.get(`/workers/${workerId}/performance?period=${period}`);
            return res.data;
        } catch (e) {
            console.error('Performance error:', e);
            return null;
        }
    },
    recalculate: async (workerId, period = 'month') => {
        try {
            const res = await productionApi.post(`/workers/${workerId}/recalculate?period=${period}`);
            return res.data;
        } catch (e) {
            console.error('Recalculate error:', e);
            return null;
        }
    },
    getMonthlyRatings: async (month) => {
        try {
            const res = await productionApi.get(`/workers/monthly-ratings?month=${month}`);
            return res.data;
        } catch (e) {
            console.error('Monthly ratings error:', e);
            return { success: false, data: [] };
        }
    },
    recalculateAllRatings: async (month) => {
        try {
            const res = await productionApi.post(`/workers/recalculate-all-ratings`, { month });
            return res.data;
        } catch (e) {
            console.error('Recalculate all ratings error:', e);
            return { success: false };
        }
    },
    getRatingHistory: async (workerId, months = 12) => {
        try {
            const res = await productionApi.get(`/workers/${workerId}/rating-history?months=${months}`);
            return res.data;
        } catch (e) {
            console.error('Rating history error:', e);
            return { success: false, data: [] };
        }
    }
};

// --- STAR RENDERER ---
const StarRating = ({ stars, size = 20 }) => {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    size={size}
                    className={i <= stars ? 'text-amber-400' : 'text-slate-200'}
                    fill={i <= stars ? 'currentColor' : 'none'}
                    strokeWidth={i <= stars ? 0 : 1.5}
                />
            ))}
        </div>
    );
};

const PartialStarRating = ({ value = 0, size = 18 }) => {
    const safeValue = Math.max(0, Math.min(5, Number(value || 0)));
    return (
        <div className="flex items-center gap-1">
            {[0, 1, 2, 3, 4].map((idx) => {
                const fillPct = Math.max(0, Math.min(1, safeValue - idx)) * 100;
                return (
                    <div key={idx} className="relative" style={{ width: size, height: size }}>
                        <Star size={size} className="text-slate-300 absolute inset-0" fill="none" />
                        <div className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                            <Star size={size} className="text-[#f4b740]" fill="#f4b740" strokeWidth={0} />
                        </div>
                    </div>
                );
            })}
            <span className="text-xs font-bold text-slate-600 ml-1">{safeValue.toFixed(2)}</span>
        </div>
    );
};

// --- RANK BADGE ---
const RankBadge = ({ rank }) => {
    if (rank === 1) return (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg shadow-amber-200">
            <Crown size={20} className="text-white" />
        </div>
    );
    if (rank === 2) return (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-lg shadow-slate-200">
            <Medal size={20} className="text-white" />
        </div>
    );
    if (rank === 3) return (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-200">
            <Award size={20} className="text-white" />
        </div>
    );
    return (
        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
            <span className="text-sm font-bold text-slate-500">#{rank}</span>
        </div>
    );
};

// --- METRIC CARD ---
const MetricCard = ({ icon: Icon, label, value, suffix = '', color = 'indigo', trend }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col">
        <div className="flex items-center justify-between mb-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${color}-50`}>
                <Icon size={18} className={`text-${color}-600`} />
            </div>
            {trend !== undefined && (
                <div className={`flex items-center gap-0.5 text-xs font-bold ${trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {trend > 0 ? <ArrowUpRight size={12} /> : trend < 0 ? <ArrowDownRight size={12} /> : <Minus size={12} />}
                    {Math.abs(trend)}%
                </div>
            )}
        </div>
        <div className="text-2xl font-bold text-slate-800">{value}<span className="text-sm text-slate-400 ml-1">{suffix}</span></div>
        <div className="text-[10px] font-bold text-slate-400 uppercase mt-1">{label}</div>
    </div>
);

// --- MINI BAR CHART (pure CSS) ---
const MiniBarChart = ({ data, dataKey, label, color = 'bg-indigo-500', maxBars = 14 }) => {
    const displayData = data.slice(-maxBars);
    if (displayData.length === 0) return (
        <div className="text-center py-6 text-slate-400 text-xs">No activity data yet</div>
    );
    const max = Math.max(...displayData.map(d => d[dataKey] || 0), 1);
    return (
        <div>
            <div className="text-xs font-bold text-slate-500 uppercase mb-3">{label}</div>
            <div className="flex items-end gap-1 h-24">
                {displayData.map((d, i) => {
                    const val = d[dataKey] || 0;
                    const pct = Math.max((val / max) * 100, 4);
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center group relative">
                            <div
                                className={`w-full ${color} rounded-t transition-all hover:opacity-80`}
                                style={{ height: `${pct}%`, minHeight: '2px' }}
                                title={`${d.date}: ${val}`}
                            />
                            {/* Tooltip */}
                            <div className="absolute -top-8 bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded hidden group-hover:block whitespace-nowrap z-10">
                                {d.date?.slice(5)}: {val}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-between text-[9px] text-slate-400 mt-1 font-mono">
                <span>{displayData[0]?.date?.slice(5)}</span>
                <span>{displayData[displayData.length - 1]?.date?.slice(5)}</span>
            </div>
        </div>
    );
};


// === LEADERBOARD SCREEN ===
const LeaderboardScreen = ({ onSelectWorker }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    const [searchQuery, setSearchQuery] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        const res = await WorkerService.getLeaderboard(period);
        setData(res?.data || []);
        setLoading(false);
    }, [period]);

    useEffect(() => { loadData(); }, [loadData]);

    const filteredData = data.filter(w => {
        const q = searchQuery.toLowerCase();
        return (
            (w.worker_name || '').toLowerCase().includes(q) ||
            (w.worker_code || '').toLowerCase().includes(q) ||
            (w.role || '').toLowerCase().includes(q)
        );
    });

    // Summary stats
    const totalWorkers = data.length;
    const avgStars = totalWorkers > 0 ? (data.reduce((s, w) => s + w.stars, 0) / totalWorkers).toFixed(1) : 0;
    const bonusEligible = data.filter(w => w.bonus_eligible).length;
    const totalPieces = data.reduce((s, w) => s + (w.pieces_produced || 0), 0);

    return (
        <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                            <Trophy className="text-amber-500" size={28} /> Worker Performance Leaderboard
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">Track stars, KPIs & daily activity for every worker</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            className="px-4 py-2 border border-slate-300 rounded-lg bg-white text-sm font-medium outline-none focus:ring-2 focus:ring-amber-500"
                            value={period}
                            onChange={e => setPeriod(e.target.value)}
                        >
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="quarter">This Quarter</option>
                        </select>
                        <button onClick={loadData} className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors text-slate-500" title="Refresh">
                            <RefreshCcw size={18} />
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4 mt-5">
                    <MetricCard icon={Users} label="Active Workers" value={totalWorkers} color="indigo" />
                    <MetricCard icon={Star} label="Avg Stars" value={avgStars} suffix="/ 5" color="amber" />
                    <MetricCard icon={Award} label="Bonus Eligible" value={bonusEligible} color="emerald" />
                    <MetricCard icon={Package} label="Total Pieces (Period)" value={totalPieces.toLocaleString()} color="blue" />
                </div>

                {/* Search */}
                <div className="mt-4 relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search by name, code, or role..."
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Leaderboard Table */}
            <div className="flex-1 overflow-y-auto p-8">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                        <Users size={40} className="mb-3 text-slate-300" />
                        <p className="font-medium">No worker performance data found</p>
                        <p className="text-xs mt-1">Complete stages to generate performance metrics</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Rank</th>
                                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Worker</th>
                                    <th className="text-center px-6 py-3 text-xs font-bold text-slate-500 uppercase">Stars</th>
                                    <th className="text-center px-6 py-3 text-xs font-bold text-slate-500 uppercase">Pieces</th>
                                    <th className="text-center px-6 py-3 text-xs font-bold text-slate-500 uppercase">On-Time %</th>
                                    <th className="text-center px-6 py-3 text-xs font-bold text-slate-500 uppercase">QC Pass %</th>
                                    <th className="text-center px-6 py-3 text-xs font-bold text-slate-500 uppercase">Stages</th>
                                    <th className="text-center px-6 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                                    <th className="text-right px-6 py-3 text-xs font-bold text-slate-500 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredData.map(w => (
                                    <tr
                                        key={w.worker_id}
                                        className="hover:bg-amber-50/30 transition-colors cursor-pointer group"
                                        onClick={() => onSelectWorker(w)}
                                    >
                                        <td className="px-6 py-4">
                                            <RankBadge rank={w.rank} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                                                    {(w.worker_name || 'W')[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-800">{w.worker_name}</div>
                                                    <div className="text-xs text-slate-500 flex items-center gap-2">
                                                        <span className="font-mono bg-slate-100 px-1.5 rounded">{w.worker_code}</span>
                                                        <span>{w.role}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <StarRating stars={w.stars} size={16} />
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="font-bold text-slate-800">{w.pieces_produced}</span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`font-bold ${w.on_time_rate >= 90 ? 'text-emerald-600' : w.on_time_rate >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                                                {w.on_time_rate}%
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`font-bold ${w.qc_pass_rate >= 95 ? 'text-emerald-600' : w.qc_pass_rate >= 85 ? 'text-amber-600' : 'text-red-600'}`}>
                                                {w.qc_pass_rate}%
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-slate-600 font-medium">{w.total_stages}</span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {w.bonus_eligible ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold border border-emerald-200">
                                                    <Award size={12} /> Bonus Eligible
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                                                    Standard
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <ChevronRight size={18} className="text-slate-300 group-hover:text-amber-500 transition-colors" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};


const MonthlyRatingsScreen = () => {
    const [month, setMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recalcLoading, setRecalcLoading] = useState(false);
    const [expandedWorkerId, setExpandedWorkerId] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const res = await WorkerService.getMonthlyRatings(month);
        if (res?.success) {
            const normalized = (res.data || []).map((row) => ({
                ...row,
                score: Number(row.score ?? row.weighted_score ?? 0),
                pieces_completed: Number(row.pieces_completed ?? row.pieces_total ?? 0),
                qc_pass_rate: Number(row.qc_pass_rate ?? 0),
                attendance_rate: Number(row.attendance_rate ?? row?.breakdown?.factors?.attendance_rate ?? 0),
                on_time_rate: Number(row.on_time_rate ?? row?.breakdown?.factors?.on_time_rate ?? 0),
                design_breakdown: Array.isArray(row?.breakdown?.design_breakdown) ? row.breakdown.design_breakdown : (row.design_breakdown || []),
            }));
            setRows(normalized);
            setError('');
        } else {
            setRows([]);
            setError(res?.message || 'Failed to load monthly ratings');
        }
        setLoading(false);
    }, [month]);

    useEffect(() => { load(); }, [load]);

    const recalculateAll = async () => {
        setRecalcLoading(true);
        const res = await WorkerService.recalculateAllRatings(month);
        setRecalcLoading(false);
        if (res?.success) {
            await load();
        } else {
            setError(res?.message || 'Failed to recalculate monthly ratings');
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
            <div className="px-8 py-6 border-b border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <BarChart3 className="text-amber-500" size={24} /> Monthly Rating Report
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">Weighted by design complexity and QC quality</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        <button
                            onClick={load}
                            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium"
                        >
                            Refresh
                        </button>
                        <button
                            onClick={recalculateAll}
                            disabled={recalcLoading}
                            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white rounded-lg text-sm font-bold"
                        >
                            {recalcLoading ? 'Calculating...' : 'Recalculate All'}
                        </button>
                    </div>
                </div>
                {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
            </div>

            <div className="flex-1 overflow-y-auto p-8">
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[980px] text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Worker Name</th>
                                        <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Department</th>
                                        <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Stars</th>
                                        <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase">Score</th>
                                        <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase">Pieces</th>
                                        <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase">QC Pass Rate</th>
                                        <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase">Attendance</th>
                                        <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase">On-Time</th>
                                        <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Top Design</th>
                                        <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {rows.map((row) => (
                                        <React.Fragment key={row.worker_id}>
                                            <tr className="hover:bg-slate-50/80">
                                                <td className="px-5 py-3">
                                                    <div className="font-bold text-slate-800">{row.worker_name}</div>
                                                    <div className="text-xs text-slate-500">{row.worker_code}</div>
                                                </td>
                                                <td className="px-5 py-3 text-slate-600">{row.department || '-'}</td>
                                                <td className="px-5 py-3"><PartialStarRating value={row.stars} /></td>
                                                <td className="px-5 py-3 text-right font-bold text-indigo-700">{Number(row.score || 0).toFixed(2)}</td>
                                                <td className="px-5 py-3 text-right">{row.pieces_completed || 0}</td>
                                                <td className="px-5 py-3 text-right">{Number(row.qc_pass_rate || 0).toFixed(2)}%</td>
                                                <td className="px-5 py-3 text-right">{Number(row.attendance_rate || 0).toFixed(2)}%</td>
                                                <td className="px-5 py-3 text-right">{Number(row.on_time_rate || 0).toFixed(2)}%</td>
                                                <td className="px-5 py-3">{row.top_design || row.design_breakdown?.[0]?.design_code || '-'}</td>
                                                <td className="px-5 py-3 text-right">
                                                    <button
                                                        onClick={() => setExpandedWorkerId(expandedWorkerId === row.worker_id ? null : row.worker_id)}
                                                        className="text-xs font-bold text-amber-600 hover:text-amber-700"
                                                    >
                                                        {expandedWorkerId === row.worker_id ? 'Hide Details' : 'View Details'}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedWorkerId === row.worker_id && (
                                                <tr>
                                                    <td colSpan={10} className="px-5 py-3 bg-amber-50/50">
                                                        <div className="text-xs font-bold uppercase text-slate-500 mb-2">Design Complexity Breakdown</div>
                                                        {Array.isArray(row.design_breakdown) && row.design_breakdown.length > 0 ? (
                                                            <div className="overflow-x-auto">
                                                                <table className="w-full text-xs">
                                                                    <thead>
                                                                        <tr className="text-slate-500 border-b border-slate-200">
                                                                            <th className="text-left py-2">Design</th>
                                                                            <th className="text-right py-2">Pieces</th>
                                                                            <th className="text-right py-2">Weight</th>
                                                                            <th className="text-right py-2">Weighted</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {row.design_breakdown.map((item, idx) => (
                                                                            <tr key={`${row.worker_id}-${idx}`} className="border-b border-slate-100">
                                                                                <td className="py-2">{item.design_code || item.design_name || 'â€”'}</td>
                                                                                <td className="py-2 text-right">{item.pieces || 0}</td>
                                                                                <td className="py-2 text-right">{Number(item.complexity_weight || 1).toFixed(2)}</td>
                                                                                <td className="py-2 text-right font-semibold text-indigo-700">{Number(item.weighted_pieces || 0).toFixed(2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs text-slate-500">No design activity found for this month.</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    {!rows.length && (
                                        <tr>
                                            <td colSpan={10} className="px-5 py-10 text-center text-slate-500">
                                                No monthly rating rows found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


// === WORKER DETAIL SCREEN ===
const WorkerDetailScreen = ({ worker, onBack }) => {
    const [perfData, setPerfData] = useState(null);
    const [ratingHistory, setRatingHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    const [recalculating, setRecalculating] = useState(false);

    const loadDetail = useCallback(async () => {
        setLoading(true);
        const [perfRes, historyRes] = await Promise.all([
            WorkerService.getPerformance(worker.worker_id, period),
            WorkerService.getRatingHistory(worker.worker_id, 12)
        ]);
        if (perfRes?.success) setPerfData(perfRes);
        setRatingHistory(historyRes?.success ? (historyRes.data || []) : []);
        setLoading(false);
    }, [worker.worker_id, period]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    const handleRecalculate = async () => {
        setRecalculating(true);
        const res = await WorkerService.recalculate(worker.worker_id, period);
        if (res?.success) {
            await loadDetail();
        }
        setRecalculating(false);
    };

    if (loading) return (
        <div className="p-6">
            <SkeletonLoader count={5} height={64} />
        </div>
    );

    const metrics = perfData?.metrics || {};
    const daily = perfData?.daily_activity || [];

    return (
        <div className="flex flex-col h-full bg-slate-50/50 overflow-hidden">
            {/* Header */}
            <div className="px-8 py-5 border-b border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors">
                            <ArrowLeft size={20} />
                        </button>
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                            {(perfData?.worker_name || worker.worker_name || 'W')[0].toUpperCase()}
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-3">
                                {perfData?.worker_name || worker.worker_name}
                                {perfData?.bonus_eligible && (
                                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full font-bold border border-emerald-200 flex items-center gap-1">
                                        <Award size={12} /> Bonus Eligible
                                    </span>
                                )}
                            </h1>
                            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                                <span className="font-mono bg-slate-100 px-1.5 rounded">{perfData?.worker_code || worker.worker_code}</span>
                                <span>{perfData?.role || worker.role}</span>
                                <span className="text-indigo-600 font-medium">Period: {perfData?.period_start} â†’ {perfData?.period_end}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <select
                            className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm font-medium outline-none focus:ring-2 focus:ring-amber-500"
                            value={period}
                            onChange={e => setPeriod(e.target.value)}
                        >
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                            <option value="quarter">Quarter</option>
                        </select>
                        <ApiButton
                            onClick={handleRecalculate}
                            loading={recalculating}
                            variant="success"
                            className="text-sm"
                        >
                            <RefreshCcw size={14} />
                            {recalculating ? 'Calculating...' : 'Recalculate'}
                        </ApiButton>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-6xl mx-auto space-y-6">

                    {/* Stars + Appraisal */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center gap-8">
                        <div className="flex flex-col items-center">
                            <StarRating stars={perfData?.stars || 0} size={32} />
                            <div className="text-3xl font-black text-amber-500 mt-2">{perfData?.stars || 0}<span className="text-lg text-slate-400">/5</span></div>
                            <div className="text-xs text-slate-400 font-bold uppercase mt-1">Overall Rating</div>
                        </div>
                        <div className="flex-1 bg-amber-50 rounded-xl p-4 border border-amber-100">
                            <div className="text-xs font-bold text-amber-700 uppercase mb-1 flex items-center gap-1">
                                <Activity size={12} /> Performance Summary
                            </div>
                            <p className="text-sm text-amber-900 leading-relaxed">
                                {perfData?.appraisal_summary || 'No appraisal data available yet.'}
                            </p>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-5 gap-4">
                        <MetricCard icon={Target} label="On-Time Rate" value={`${metrics.on_time_rate || 0}`} suffix="%" color="emerald" />
                        <MetricCard icon={CheckCircle2} label="QC Pass Rate" value={`${metrics.qc_pass_rate || 0}`} suffix="%" color="blue" />
                        <MetricCard icon={Layers} label="Stages Completed" value={metrics.total_stages || 0} color="indigo" />
                        <MetricCard icon={Package} label="Pieces Produced" value={metrics.pieces_produced || 0} color="purple" />
                        <MetricCard icon={Clock} label="Avg Delay" value={`${metrics.avg_delay_minutes || 0}`} suffix="min" color="amber" />
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                        <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2 mb-3">
                            <TrendingUp size={16} /> Rating History (Last 12 Months)
                        </h3>
                        {!ratingHistory.length ? (
                            <div className="text-xs text-slate-500 py-4">No monthly rating history available yet.</div>
                        ) : (
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={toRatingHistorySeries(ratingHistory)}
                                        margin={{ top: 8, right: 12, left: -20, bottom: 4 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            formatter={(value) => [Number(value).toFixed(2), 'Stars']}
                                            contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }}
                                        />
                                        <Line type="monotone" dataKey="stars" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                            <MiniBarChart
                                data={daily}
                                dataKey="stages_completed"
                                label="Stages Completed / Day"
                                color="bg-indigo-500"
                                maxBars={30}
                            />
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                            <MiniBarChart
                                data={daily}
                                dataKey="pieces_produced"
                                label="Pieces Produced / Day"
                                color="bg-emerald-500"
                                maxBars={30}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                            <MiniBarChart
                                data={daily}
                                dataKey="total_work_minutes"
                                label="Work Duration / Day (minutes)"
                                color="bg-blue-500"
                                maxBars={30}
                            />
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                            <MiniBarChart
                                data={daily}
                                dataKey="stages_delayed"
                                label="Delayed Stages / Day"
                                color="bg-red-400"
                                maxBars={30}
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                        <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2 mb-3">
                            <Layers size={16} /> Design Complexity Breakdown
                        </h3>
                        {Array.isArray(perfData?.design_complexity_breakdown) && perfData.design_complexity_breakdown.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                                            <th className="text-left py-2">Design</th>
                                            <th className="text-right py-2">Pieces</th>
                                            <th className="text-right py-2">Weight</th>
                                            <th className="text-right py-2">Weighted Score</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {perfData.design_complexity_breakdown.map((row, idx) => (
                                            <tr key={`${worker.worker_id}-design-${idx}`} className="border-b border-slate-100">
                                                <td className="py-2">{row.design_code || row.design_name || 'â€”'}</td>
                                                <td className="py-2 text-right">{row.pieces || 0}</td>
                                                <td className="py-2 text-right">{Number(row.complexity_weight || 1).toFixed(2)}</td>
                                                <td className="py-2 text-right font-bold text-indigo-700">{Number(row.weighted_pieces || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-xs text-slate-500">No design complexity breakdown available for this worker yet.</div>
                        )}
                    </div>

                    {/* Recent Daily Activity Table */}
                    {daily.length > 0 && (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                                <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2">
                                    <Calendar size={16} /> Daily Activity Log
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50/50 border-b border-slate-100">
                                            <th className="text-left px-5 py-2 text-xs font-bold text-slate-500 uppercase">Date</th>
                                            <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Stages</th>
                                            <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Pieces</th>
                                            <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Delayed</th>
                                            <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Work Mins</th>
                                            <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Wastage (g)</th>
                                            <th className="text-left px-5 py-2 text-xs font-bold text-slate-500 uppercase">Products</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {[...daily].reverse().slice(0, 14).map(d => (
                                            <tr key={d.date} className="hover:bg-slate-50/50">
                                                <td className="px-5 py-2.5 font-mono text-xs font-bold text-slate-600">{d.date}</td>
                                                <td className="px-5 py-2.5 text-center font-bold text-indigo-600">{d.stages_completed}</td>
                                                <td className="px-5 py-2.5 text-center font-bold text-emerald-600">{d.pieces_produced}</td>
                                                <td className="px-5 py-2.5 text-center">
                                                    <span className={`font-bold ${d.stages_delayed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                                        {d.stages_delayed}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-2.5 text-center text-slate-600">{d.total_work_minutes}</td>
                                                <td className="px-5 py-2.5 text-center text-slate-600">{d.total_wastage_grams.toFixed(1)}</td>
                                                <td className="px-5 py-2.5 text-xs text-slate-500 max-w-[200px] truncate">
                                                    {(d.products_worked_on || []).map(p => p.stage).join(', ') || 'â€”'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// === MAIN APP SHELL ===
export default function WorkerPerformanceDashboard() {
    const navigate = useNavigate();
    const [view, setView] = useState('leaderboard'); // leaderboard | monthly | detail
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleSelectWorker = (worker) => {
        setSelectedWorker(worker);
        setView('detail');
    };

    const handleBack = () => {
        setView('leaderboard');
        setSelectedWorker(null);
    };

    return (
        <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
            <style>{`
                @media (max-width: ${BREAKPOINTS.mobile}) {
                    .wp-sidebar { position: fixed; inset: 0 auto 0 0; width: 250px; transform: translateX(-100%); transition: transform .25s ease; z-index: 40; }
                    .wp-sidebar.open { transform: translateX(0); }
                }
            `}</style>
            {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}
            {/* Sidebar */}
            <aside className={`wp-sidebar w-64 bg-gradient-to-b from-amber-900 via-orange-900 to-slate-900 text-white flex flex-col shadow-2xl z-20 ${sidebarOpen ? 'open' : ''}`}>
                <div className="p-6 border-b border-white/10 flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Gem className="text-white" size={24} />
                    </div>
                    <div>
                        <span className="text-xl font-black text-white tracking-tighter uppercase italic">
                            ZILVERRA<span className="text-amber-400">JEWELS</span>
                        </span>
                    </div>
                </div>
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    <div className="text-xs font-bold text-amber-300 uppercase px-3 mt-2 mb-2">Performance</div>

                    <button
                        onClick={() => { setView('leaderboard'); setSelectedWorker(null); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${view === 'leaderboard' ? 'bg-white/10 text-white' : 'text-amber-200 hover:bg-white/5'}`}
                    >
                        <Trophy size={20} className={view === 'leaderboard' ? 'text-amber-400' : ''} /> Leaderboard
                    </button>
                    <button
                        onClick={() => { setView('monthly'); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${view === 'monthly' ? 'bg-white/10 text-white' : 'text-amber-200 hover:bg-white/5'}`}
                    >
                        <BarChart3 size={20} className={view === 'monthly' ? 'text-amber-400' : ''} /> Monthly Rating Report
                    </button>

                    <button
                        onClick={() => { if (selectedWorker) setView('detail'); }}
                        disabled={!selectedWorker}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${view === 'detail' ? 'bg-white/10 text-white' : 'text-amber-200 hover:bg-white/5'} ${!selectedWorker ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                        <User size={20} className={view === 'detail' ? 'text-amber-400' : ''} /> Worker Detail
                    </button>

                    <div className="text-xs font-bold text-amber-300 uppercase px-3 mt-6 mb-2">Navigation</div>
                    <button
                        onClick={() => navigate('/production')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-amber-200 hover:bg-white/5 transition-all"
                    >
                        <ArrowLeft size={20} /> Back to Production
                    </button>
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50/50">
                {/* Top bar */}
                <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 shadow-sm z-10">
                    <div className="flex items-center gap-2 text-sm">
                        <button className="md:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setSidebarOpen(true)}>
                            <Menu size={18} />
                        </button>
                        <span onClick={() => navigate('/production')} className="text-slate-500 hover:text-amber-600 cursor-pointer transition-colors">Home</span>
                        <span className="text-slate-300">/</span>
                        <span className="font-medium text-slate-800">Worker Performance</span>
                        {view === 'detail' && selectedWorker && (
                            <>
                                <span className="text-slate-300">/</span>
                                <span className="font-medium text-amber-600">{selectedWorker.worker_name}</span>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold border border-emerald-100 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span> System Online
                        </span>
                    </div>
                </header>

                {/* Content */}
                <div className="flex-1 overflow-hidden relative">
                    {view === 'leaderboard' && (
                        <LeaderboardScreen onSelectWorker={handleSelectWorker} />
                    )}
                    {view === 'monthly' && (
                        <MonthlyRatingsScreen />
                    )}
                    {view === 'detail' && selectedWorker && (
                        <WorkerDetailScreen worker={selectedWorker} onBack={handleBack} />
                    )}
                </div>
            </main>
        </div>
    );
}
